import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Loader2, MailWarning, RefreshCw, ChevronRight, Reply, EyeOff,
} from 'lucide-react';

const fmtDt = (iso) => iso
  ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '';

export function UnansweredBrokerMails({ onDiscussionAdded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(true);

  const load = () => {
    setLoading(true);
    api.getUnansweredBrokerMails(60)
      .then(r => setData(r.data))
      .catch(err => setData({ mails: [], error: err.response?.data?.detail || 'Scan fehlgeschlagen' }))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const mails = data?.mails || [];

  const takeAsDiscussion = async (m) => {
    setBusy(m.message_id);
    try {
      await api.addMailAsDiscussion(m.message_id);
      toast.success(`„${m.from_name}" in Broker Discussions übernommen — KI-Antwort dort verfassen`);
      setData(d => ({ ...d, mails: d.mails.filter(x => x.message_id !== m.message_id) }));
      onDiscussionAdded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Übernahme fehlgeschlagen');
    }
    setBusy('');
  };

  const ignore = async (m) => {
    setBusy(m.message_id);
    try {
      await api.ignoreUnansweredMail(m.internet_message_id);
      setData(d => ({ ...d, mails: d.mails.filter(x => x.message_id !== m.message_id) }));
      toast.success('Ausgeblendet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehlgeschlagen');
    }
    setBusy('');
  };

  return (
    <div className="bg-card border border-border rounded-sm" data-testid="unanswered-broker-mails">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 px-4 py-3 text-left"
        data-testid="unanswered-mails-toggle">
        <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
        <MailWarning className="w-4 h-4 text-amber-400" />
        <span className="text-sm font-medium text-foreground">Unbeantwortete Broker-Mails</span>
        {!loading && (
          <span className={`text-[10px] font-mono border rounded px-1.5 py-0.5 ${mails.length > 0
            ? 'border-amber-500/40 bg-amber-500/10 text-amber-300' : 'border-border text-muted-foreground'}`}
            data-testid="unanswered-mails-count">{mails.length}</span>
        )}
        <span className="text-[10px] text-muted-foreground hidden sm:block">letzte {data?.days || 60} Tage · ohne Auto-Replies</span>
        <span className="ml-auto flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <button onClick={load} disabled={loading} data-testid="unanswered-mails-refresh"
            className="p-1.5 text-muted-foreground hover:text-foreground transition-colors" title="Neu scannen">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </span>
      </button>
      {open && (
        <div className="border-t border-border">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground text-xs">
              <Loader2 className="w-4 h-4 animate-spin" /> Scanne Postfach (bis zu {data?.days || 60} Tage zurück)…
            </div>
          ) : data?.error ? (
            <p className="text-xs text-amber-400 px-4 py-4" data-testid="unanswered-mails-error">{data.error}</p>
          ) : mails.length === 0 ? (
            <p className="text-xs text-muted-foreground px-4 py-4" data-testid="unanswered-mails-empty">
              Alles beantwortet — keine offenen Broker-Mails gefunden. 🎉
            </p>
          ) : (
            <div className="divide-y divide-border/60 max-h-96 overflow-y-auto">
              {mails.map((m, i) => (
                <div key={m.message_id} className="px-4 py-2.5" data-testid={`unanswered-mail-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-foreground">{m.from_name}</span>
                    <span className="text-[10px] text-muted-foreground">{m.from_email}</span>
                    <span className="text-[10px] text-muted-foreground ml-auto shrink-0">{fmtDt(m.received_at)}</span>
                  </div>
                  <p className="text-xs text-foreground/85 mt-0.5 truncate">{m.subject}</p>
                  {m.preview && <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{m.preview}</p>}
                  <div className="flex items-center gap-2 mt-1.5">
                    <button onClick={() => takeAsDiscussion(m)} disabled={busy === m.message_id}
                      data-testid={`unanswered-reply-btn-${i}`}
                      className="flex items-center gap-1 text-[11px] border border-emerald-500/40 rounded-sm px-2 py-1 text-emerald-300 hover:bg-emerald-500/15 transition-colors">
                      {busy === m.message_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Reply className="w-3 h-3" />}
                      Antworten (als Discussion)
                    </button>
                    <button onClick={() => ignore(m)} disabled={busy === m.message_id}
                      data-testid={`unanswered-ignore-btn-${i}`}
                      className="flex items-center gap-1 text-[11px] border border-border rounded-sm px-2 py-1 text-muted-foreground hover:text-foreground transition-colors">
                      <EyeOff className="w-3 h-3" /> Ignorieren
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
